import {
  animate,
  query,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Component } from '@angular/core';
import { FeatureTogglesFacade } from '@cigna/features';
import { OmniChatFacade } from '@cigna/omni/chat-state';
import { OmniConversationsFacade } from '@cigna/omni/conversations-state';
import { OmniAnalyticsService } from '@cigna/omni/shared';
import { combineLatest } from 'rxjs';
import { map, tap, withLatestFrom } from 'rxjs/operators';
import { DcmDialogComponent } from '../dcm-dialog/dcm-dialog.component';
import { DialogComponent } from '../dialog/dialog.component';
import { HistoryComponent } from '../history/history.component';
import { HomeComponent } from '../home/home.component';
import { SplashScreenComponent } from '../splash-screen/splash-screen.component';
import { ActiveDMsComponent } from '../active-dms/active-dms.component';

const ANIMATION_TIME = 300;
const QUERY_ABSOLUTE = query(
  ':enter, :leave',
  style({
    display: 'block',
    left: 0,
    position: 'absolute',
    top: '48px', // Height of the header so there's no jumping in the animation
    width: '100%',
  }),
  { optional: true },
);
const STYLE_IN = style({ transform: 'translateX(0)', opacity: 1 });
const STYLE_RIGHT = style({ transform: 'translateX(120%)', opacity: 0 });
const STYLE_LEFT = style({ transform: 'translateX(-120%)', opacity: 0 });

@Component({
  selector: 'cigna-omni-chat-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
  animations: [
    trigger('shell', [
      state('void', style({ transform: 'translateY(100%)', opacity: 0 })),
      transition(':enter, :leave', [animate(ANIMATION_TIME)]),
    ]),
    trigger('flyInOut', [
      transition('left => right, left2 => right, right <=> right2', [
        QUERY_ABSOLUTE,
        query(':enter', [STYLE_RIGHT], { optional: true }),
        query(':leave', [STYLE_IN, animate(ANIMATION_TIME, STYLE_LEFT)], {
          optional: true,
        }),
        query(':enter', [STYLE_RIGHT, animate(ANIMATION_TIME, STYLE_IN)], {
          optional: true,
        }),
      ]),
      transition('right => left, right2 => left, left <=> left2', [
        QUERY_ABSOLUTE,
        query(':enter', [STYLE_LEFT], { optional: true }),
        query(':leave', [STYLE_IN, animate(ANIMATION_TIME, STYLE_RIGHT)], {
          optional: true,
        }),
        query(':enter', [STYLE_LEFT, animate(ANIMATION_TIME, STYLE_IN)], {
          optional: true,
        }),
      ]),
    ]),
  ],
})
export class ShellComponent {
  public animationState$ = this.facade.direction$;
  public currentComponent$ = this.facade.currentComponent$.pipe(
    withLatestFrom(this.features.featuresEnabled(['isDcmChat'])),
    map(([component, isDcmChat]) => {
      switch (component) {
        case 'splashView':
          return SplashScreenComponent;

        case 'activeDMsView':
          return ActiveDMsComponent;

        case 'dialog':
          if (isDcmChat) {
            return DcmDialogComponent;
          }
          return DialogComponent;

        case 'history':
          return HistoryComponent;

        case 'home':
        default:
          return HomeComponent;
      }
    }),
  );
  public isShellOpen$ = this.facade.isShellOpen$;
  public isShellMinimized$ = this.facade.isShellMinimized$;
  public isShellOpenOrMinimized$ = combineLatest(
    this.isShellOpen$,
    this.isShellMinimized$,
  ).pipe(
    tap((res) => (this.shouldHideHeader = res[1] ? res[1] : false)),
    map(([isOpen, isMinimized]) => isOpen || isMinimized),
  );
  shouldHideHeader: boolean;
  public shouldEnablePreChatWelcome$ = this.features.featuresEnabled([
    'enableNewPreChatWelcome',
  ]);
  public enableNewPreChatWelcome$ = this.features.featuresEnabled([
    'enableNewPreChatWelcome',
  ]);
  canUpdateAnalyticsData$ = this.features.featuresEnabled([
    'enableChatAnalyticsDataLayer',
  ]);
  isDcmChat$ = this.features.featuresEnabled(['isDcmChat']);

  public isShowHistory$ = this.facade.isHistory$;
  public existingConvId = this.conversationsFacade.existingConversationId$;
  public closeSplashScreen$ = this.conversationsFacade.closeSplashScreen$;
  public shouldFocusOnHeader$ = this.conversationsFacade.shouldFocusOnHeader$;

  constructor(
    private facade: OmniChatFacade,
    private features: FeatureTogglesFacade,
    private conversationsFacade: OmniConversationsFacade,
    private analytics: OmniAnalyticsService,
  ) {}

  printTranscript(canUpdateAnalyticsData: boolean) {
    const innerContents = document.querySelector('#printSection')?.innerHTML;
    if (innerContents) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.body.innerHTML = innerContents;
        printWindow.document.head.innerHTML = document.head.innerHTML;
        // adding setTimeout to support print in Safari
        setTimeout(() => {
          try {
            printWindow.document.execCommand('print', false);
          } catch (e) {
            window.print();
          }
          printWindow.document.close();
          printWindow.focus();
          printWindow.close();
        }, 200);
      }
    }
    if (canUpdateAnalyticsData) {
      // todo: update to new use data layer format standard
      this.analytics.dataLayer.updateAdobeDataLayer({
        event: 'pageAction',
        actionData: {
          actionName: 'downloadTranscript',
          hitType: 'action',
          widgetName: 'chat-download-transcript',
          customLinkText: 'Chat Transcript',
          requirementId: 'ac24072',
          userStory: 'ODCTC-593',
        },
      });
    }
  }
}
